/**
 * Pricing for the one-off ("prepay") checkout used by the DE and NL markets,
 * where a booking is paid up front instead of submitted as a request.
 *
 * Deliberately NOT part of `bloodMembershipPricing`: that module charges the
 * baseline blood test at the YEARLY MEMBERSHIP price, because in Switzerland
 * the blood test IS the membership. DE/NL have no membership, so every line is
 * simply its own market list price. Reusing the membership helpers here would
 * silently charge the wrong amount.
 *
 * Kept dependency-free so it can be exercised without booting the app (see
 * scripts/check-oneoff-pricing.ts).
 */

/** Structural minimum these functions read off a product. */
export type PricedProduct = {
  id?: string;
  name?: string;
  stripe_product_id?: string;
  stripe_metadata_id?: string | null;
  default_price?: { unit_amount?: number | null } | null;
};

/**
 * A product's list-price contribution, in cents. `get-products` has already
 * resolved `default_price` to the market-tagged price (EUR for DE/NL), and
 * omits it entirely when a market has no price for that product — which is a
 * catalog gap the server-side reconciliation refuses, not something to paper
 * over here.
 */
export const listPriceCents = (p: PricedProduct): number =>
  p.default_price?.unit_amount ?? 0;

/**
 * Sum of the market list prices of every cart line, in cents, BEFORE any
 * voucher. This exact number is reconciled against live Stripe prices in
 * `create-booking-payment`, so it must stay a plain sum.
 */
export const oneOffSubtotalCents = (cart: PricedProduct[]): number =>
  cart.reduce((sum, p) => sum + listPriceCents(p), 0);
